// controllers/bottom_sheet_controller.js
import { Controller } from "@hotwired/stimulus";
import { debugLog, isMobileDevice } from "../utils";

export default class extends Controller {
  static targets = ["backdrop", "sheet", "container", "scrollIndicator"];

  static values = {
    closeModalDeltaY: { type: Number, default: 100 },
    redirectUrl: { type: String, default: "" },
  };

  connect() {
    debugLog("BottomSheetController: Connected");

    // Bind once so removeEventListener works
    this._onTouchStart = this.handleTouchStart.bind(this);
    this._onTouchMove  = this.handleTouchMove.bind(this);
    this._onTouchEnd   = this.handleTouchEnd.bind(this);
    this._onKeydown    = this._handleKeydown.bind(this);

    if (isMobileDevice()) {
      this.setupScrollToClose();

      if (this.hasScrollIndicatorTarget) {
        this.scrollIndicatorTarget.classList.remove("hidden");
      }
    }

      this.animateIn();

    // Optional: allow Esc to close
    document.addEventListener("keydown", this._onKeydown);
  }

  disconnect() {
    this.removeScrollToClose();
    document.removeEventListener("keydown", this._onKeydown);
  }

animateIn() {
  if (this.hasBackdropTarget) {
    this.backdropTarget.classList.add("opacity-0");
  }
  if (this.hasSheetTarget) {
    this.sheetTarget.style.transform = "translateY(16px)";
    this.sheetTarget.style.opacity = "0";
  }

  requestAnimationFrame(() => {
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("opacity-0");
    }
    if (this.hasSheetTarget) {
      this.sheetTarget.style.transform = "translateY(0)";
      this.sheetTarget.style.opacity = "1";
    }
  });
}

  setupScrollToClose() {
    if (!this.hasContainerTarget && !this.hasSheetTarget) return;

    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.isScrolling = false;

    // Listen on both container and sheet (same as your working behavior)
    const elements = [this.containerTarget, this.sheetTarget];

    elements.forEach((el) => {
      if (!el) return;
      el.addEventListener("touchstart", this._onTouchStart, { passive: true });
      el.addEventListener("touchmove",  this._onTouchMove,  { passive: true });
      el.addEventListener("touchend",   this._onTouchEnd,   { passive: true });
    });
  }

  removeScrollToClose() {
    if (!this.hasContainerTarget && !this.hasSheetTarget) return;

    const elements = [this.containerTarget, this.sheetTarget];

    elements.forEach((el) => {
      if (!el) return;
      el.removeEventListener("touchstart", this._onTouchStart);
      el.removeEventListener("touchmove",  this._onTouchMove);
      el.removeEventListener("touchend",   this._onTouchEnd);
    });
  }

  handleTouchStart(event) {
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.isScrolling = false;
    this.touchTarget = event.target;
  }

  handleTouchMove(event) {
    if (!this.touchStartY) return;

    const currentY = event.touches[0].clientY;
    const deltaY = currentY - this.touchStartY;

    // Mark as scrolling once pulled down a bit
    if (deltaY > 50) {
      this.isScrolling = true;
    }

    // Visual nudge once past threshold (use transform instead of top)
    if (deltaY > this.closeModalDeltaYValue) {
      this.sheetTarget.style.transform = "translateY(20px)";
    }
  }

  handleTouchEnd(event) {
    if (!this.touchStartY) return;

    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - this.touchStartTime;
    const currentY = event.changedTouches[0].clientY;
    const deltaY = currentY - this.touchStartY;

    // Close if pulled down past threshold for at least 250ms
    if (this.isScrolling && deltaY > this.closeModalDeltaYValue && touchDuration > 250) {
      this.closeModal();
    }

    // Reset
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.isScrolling = false;
    this.touchTarget = null;
    this.sheetTarget.style.transform = ""; // reset transform (was "top" before)
  }

  closeModal() {
    if (this.hasContainerTarget) {
      this.containerTarget.setAttribute("inert", "");
      this.containerTarget.setAttribute("aria-hidden", "true");
      this.containerTarget.classList.add("hidden");
    }
  }

  closeModalAndRedirect() {
    debugLog("BottomSheetController: Closing modal and redirecting");
    this.closeModal();

    if (this.redirectUrlValue) {
      Turbo.visit(this.redirectUrlValue);
      return;
    }
  }

  closeModalAndGoHome() {
    debugLog("BottomSheetController: Closing modal and going home");
    this.closeModal();

    Turbo.visit("/home");
  }

  close() {
    this.closeModal();
  }

  // Optional: Esc key to close
  _handleKeydown(e) {
    if (e.key === "Escape") this.closeModal();
  }
}
